import { useState } from 'react';
import { useStressTestStore } from '../../store/stressTestStore';
import { ElementStressResult } from '../../types/stressTest';
import { StressTestDetailsModal } from '../StressTestDetailsModal';
import { puToVolts, puDeviationPct, NOMINAL_VOLTAGE_LN_V } from '../../utils/electricalConstants';
import {
  VOLTAGE_OPERATIONAL_MIN_PU, VOLTAGE_OPERATIONAL_MAX_PU,
  VOLTAGE_WARNING_LOW_PU, VOLTAGE_WARNING_HIGH_PU,
  VOLTAGE_OPERATIONAL_MIN_V, VOLTAGE_OPERATIONAL_MAX_V,
  VOLTAGE_WARNING_LOW_V, VOLTAGE_WARNING_HIGH_V,
  VOLTAGE_BAND_LABEL,
} from '../../utils/voltageConstants';
import { LOADING_COMPLIANCE_LIMIT_PCT } from '../../utils/loadingConstants';
import { fmt } from '../../utils/numberFormat';
import { baseCaseLabel, collapseHeadline } from '../../utils/planningLabels';

/**
 * Results of the last planning run: base case, before/after switch, KPIs with
 * Δ, voltage band of the shown frame, confidence notes.
 */
export function ResultsSummary() {
  const run = useStressTestStore(s => s.run);
  const stressTestResults = useStressTestStore(s => s.stressTestResults);
  const resultVariant = useStressTestStore(s => s.resultVariant);
  const setResultVariant = useStressTestStore(s => s.setResultVariant);
  const isCalculating = useStressTestStore(s => s.isCalculating);
  const clearResults = useStressTestStore(s => s.clearResults);
  const isControlActive = useStressTestStore(s => s.isControlActive);

  const [showDetailsModal, setShowDetailsModal] = useState(false);

  if (!run || !stressTestResults) {
    if (isCalculating) {
      return (
        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-3">
          <p className="text-sm text-blue-600 dark:text-blue-400 flex items-center gap-2">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Berechnung...
          </p>
        </div>
      );
    }
    return null;
  }

  const { summary: frameSummary } = stressTestResults;
  const s = run.summary;
  const overloaded = Object.values(stressTestResults.results)
    .filter((r): r is ElementStressResult => r != null && r.type !== 'hausanschluss' && r.isOverloaded).length;

  const minV = frameSummary.minVoltagePu ?? 1.0;
  const maxV = frameSummary.maxVoltagePu ?? 1.0;
  const criticalCount = frameSummary.voltageViolationsCritical ?? 0;
  const warningCount = frameSummary.voltageViolationsWarning ?? 0;

  // Scale in volts from 207 to 253 V (EN 50160 band around Un = 230 V); pu values
  // are converted on the load-flow base, deviations measured from Un.
  const toPercentV = (volts: number) => Math.max(0, Math.min(100, ((volts - VOLTAGE_OPERATIONAL_MIN_V) / (VOLTAGE_OPERATIONAL_MAX_V - VOLTAGE_OPERATIONAL_MIN_V)) * 100));
  const toPercent = (v: number) => toPercentV(puToVolts(v));
  const minPos = toPercent(minV);
  const maxPos = toPercent(maxV);
  const rangeWidth = Math.max(1.5, maxPos - minPos);
  const centerPos = toPercentV(NOMINAL_VOLTAGE_LN_V);
  const tickStep = (VOLTAGE_OPERATIONAL_MAX_V - VOLTAGE_OPERATIONAL_MIN_V) / 4;
  // Red outside the EN 50160 band, orange inside the warning margin, green otherwise.
  const outOfBand = minV < VOLTAGE_OPERATIONAL_MIN_PU || maxV > VOLTAGE_OPERATIONAL_MAX_PU;
  const nearLimit = minV < VOLTAGE_WARNING_LOW_PU || maxV > VOLTAGE_WARNING_HIGH_PU;
  const rangeBg = outOfBand ? 'bg-red-500' : nearLimit ? 'bg-orange-400' : 'bg-green-400';
  const getColor = (v: number) => {
    if (v < VOLTAGE_OPERATIONAL_MIN_PU || v > VOLTAGE_OPERATIONAL_MAX_PU) return 'text-red-500';
    if (v < VOLTAGE_WARNING_LOW_PU || v > VOLTAGE_WARNING_HIGH_PU) return 'text-orange-400';
    return 'text-green-400';
  };

  const loadingDelta = s.maxLoadingWithPct - s.maxLoadingWithoutPct;
  const minVDelta = (s.minVoltageWithPu ?? 1) - (s.minVoltageWithoutPu ?? 1);
  const isSynthetic = run.baseCase.mode !== 'archive';
  const collapse = run.collapse;
  const collapseReason = run.confidence.find(c => c.factor === 'voltage_collapse')?.reason;
  // The collapse note has its own box; the other warnings stay in the list.
  const warnings = run.confidence.filter(c => c.severity === 'warning' && c.factor !== 'voltage_collapse');
  const scenarioAvailable = !collapse || collapse.hasLimitFrame;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-primary-600 bg-white dark:bg-primary-700 overflow-hidden">
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between mb-1.5">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-800 dark:text-primary-100">Ergebnis</h3>
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${stressTestResults.converged && !collapse ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
            {collapse ? 'Spannungskollaps' : stressTestResults.converged ? 'konvergiert' : 'nicht konvergiert'}
          </span>
        </div>

        {collapse && (
          <div className="mb-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-2.5 py-2" data-testid="collapse-note">
            <p className="text-xs font-semibold text-red-700 dark:text-red-300">{collapseHeadline(collapse)}</p>
            {collapseReason && <p className="mt-0.5 text-[11px] leading-snug text-red-700/90 dark:text-red-300/90">{collapseReason}</p>}
          </div>
        )}

        {/* Base case */}
        <p className={`text-[11px] mb-2 leading-snug ${isSynthetic ? 'text-amber-700 dark:text-amber-300' : 'text-gray-600 dark:text-primary-300'}`}>
          {baseCaseLabel(run)}
          {run.baseCase.mode === 'archive' && run.baseCase.trafoLoadingPct != null && (
            <span className="text-gray-400 dark:text-primary-400"> · Trafo {fmt(run.baseCase.trafoLoadingPct, 0)} %{run.baseCase.worstLineLoadingPct != null ? `, Kabel ${fmt(run.baseCase.worstLineLoadingPct, 0)} %` : ''}</span>
          )}
          {run.baseCase.fallback && <span> · Archiv nicht nutzbar ({run.baseCase.fallback})</span>}
        </p>
        {(run.calculationUnit || (run.busbarSetpoints && Object.keys(run.busbarSetpoints.scenario).length > 0)) && (
          <p className="text-[10px] text-gray-500 dark:text-primary-400 mb-2 leading-snug" data-testid="run-unit-line">
            {run.calculationUnit && (
              <>Im Netzverbund gerechnet: {run.calculationUnit.zoneIds.map(z => run.calculationUnit!.zoneNames[z] ?? z).join(', ')} ({run.calculationUnit.couplingCount} Trennstellen geschlossen). </>
            )}
            {run.busbarSetpoints && Object.keys(run.busbarSetpoints.scenario).length > 0 && (() => {
              const entries = Object.entries(run.busbarSetpoints.scenario);
              const changed = entries.filter(([id, v]) => { const b = run.busbarSetpoints!.baseline[id]; return b != null && Math.abs(b - v) > 0.0005; });
              if (entries.length > 3 && changed.length === 0) {
                return <>Sammelschienen aus dem Archiv-Frame ({entries.length} Stationen, {fmt(Math.min(...entries.map(e => e[1])), 3)}–{fmt(Math.max(...entries.map(e => e[1])), 3)} pu)</>;
              }
              const shown = changed.length > 0 ? changed : entries;
              return <>Sammelschiene {shown.map(([id, v]) => {
                const base = run.busbarSetpoints!.baseline[id];
                return `${fmt(v, 3)} pu${base != null && Math.abs(base - v) > 0.0005 ? ` (Archiv ${fmt(base, 3)})` : ''}`;
              }).join(', ')}{changed.length > 0 && changed.length < entries.length ? ` (${entries.length - changed.length} weitere unverändert)` : ''}</>;
            })()}
          </p>
        )}

        {/* Before / after switch */}
        <div className="flex rounded-lg border border-gray-300 dark:border-primary-500 overflow-hidden text-xs mb-2">
          <button
            type="button"
            onClick={() => setResultVariant('baseline')}
            className={`flex-1 px-2 py-1.5 font-medium transition-colors ${resultVariant === 'baseline' ? 'bg-gray-700 text-white dark:bg-primary-500' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-600'}`}
          >
            Ohne Änderungen
          </button>
          <button
            type="button"
            onClick={() => setResultVariant('scenario')}
            disabled={!scenarioAvailable}
            title={scenarioAvailable ? (collapse ? 'Zustand an der Tragfähigkeitsgrenze' : undefined) : 'Kein Zustand mit Änderungen: schon ohne Zubau keine Lastfluss-Lösung'}
            className={`flex-1 px-2 py-1.5 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${resultVariant === 'scenario' ? 'bg-accent-primary text-white' : 'bg-white dark:bg-primary-800 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-600'}`}
          >
            {collapse && scenarioAvailable ? 'An der Grenze' : 'Mit Änderungen'}
          </button>
        </div>

        {/* KPI grid: before → after (the "after" of a collapsing scenario is its carrying limit) */}
        {scenarioAvailable && (
        <div className="grid grid-cols-2 gap-1.5 mb-2">
          <div className={`rounded-lg p-2 text-center ${s.maxLoadingWithPct > LOADING_COMPLIANCE_LIMIT_PCT ? 'bg-red-50 dark:bg-red-900/20' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
            <p className="text-[10px] text-gray-600 dark:text-primary-400">Max. Auslastung</p>
            <p className="text-base font-bold text-gray-800 dark:text-primary-100 tabular-nums">
              {fmt(s.maxLoadingWithoutPct, 0)} → {fmt(s.maxLoadingWithPct, 0)} %
            </p>
            <p className={`text-[10px] font-semibold ${loadingDelta > 0.05 ? 'text-red-600 dark:text-red-400' : loadingDelta < -0.05 ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}>
              {loadingDelta > 0 ? '+' : ''}{fmt(loadingDelta, 1)} pp
            </p>
          </div>
          <div className={`rounded-lg p-2 text-center ${(s.minVoltageWithPu ?? 1) < VOLTAGE_OPERATIONAL_MIN_PU ? 'bg-red-50 dark:bg-red-900/20' : 'bg-blue-50 dark:bg-blue-900/20'}`}>
            <p className="text-[10px] text-gray-600 dark:text-primary-400">Min. Spannung</p>
            <p className="text-base font-bold text-gray-800 dark:text-primary-100 tabular-nums">
              {fmt(puToVolts(s.minVoltageWithoutPu ?? 1), 0)} → {fmt(puToVolts(s.minVoltageWithPu ?? 1), 0)} V
            </p>
            <p className={`text-[10px] font-semibold ${minVDelta < -0.0005 ? 'text-red-600 dark:text-red-400' : minVDelta > 0.0005 ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}>
              {minVDelta > 0 ? '+' : ''}{fmt(puToVolts(1 + minVDelta) - puToVolts(1), 1)} V
            </p>
          </div>
          <div className={`rounded-lg p-2 text-center ${s.newOverloads > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-green-50 dark:bg-green-900/20'}`}>
            <p className="text-[10px] text-gray-600 dark:text-primary-400">Überlast im Frame</p>
            <p className={`text-base font-bold tabular-nums ${overloaded > 0 ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}`}>{overloaded}</p>
            <p className="text-[10px] text-gray-500 dark:text-primary-400">
              {s.newOverloads > 0 ? `${s.newOverloads} neu` : s.resolvedOverloads > 0 ? `${s.resolvedOverloads} behoben` : 'unverändert'}
            </p>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2 text-center">
            <p className="text-[10px] text-purple-600 dark:text-purple-400">Zubau</p>
            <p className="text-base font-bold text-purple-700 dark:text-purple-300 tabular-nums">
              {s.addedLoadKw > 0 ? `+${fmt(s.addedLoadKw, 0)} kW` : s.addedGenerationKw > 0 ? `+${fmt(s.addedGenerationKw, 0)} kWp` : '–'}
            </p>
            <p className="text-[10px] text-gray-500 dark:text-primary-400">
              {s.changedHouses + s.addedHouses > 0 ? `${s.changedHouses} geändert, ${s.addedHouses} neu` : 'keine Zubauänderung'}{(s.measureCount ?? 0) > 0 ? ` · ${s.measureCount} Maßnahme${(s.measureCount ?? 0) > 1 ? 'n' : ''}` : ''}
            </p>
            {collapse && (
              <p className="text-[10px] font-semibold text-red-700 dark:text-red-300">
                getragen: {s.addedLoadKw > 0 ? `${fmt(collapse.carriedLoadKw, 0)} kW` : `${fmt(collapse.carriedGenerationKw, 0)} kWp`}
              </p>
            )}
          </div>
        </div>
        )}

        {/* Voltage band of the shown frame */}
        <div className="mb-2">
          <h4 className="text-[10px] uppercase tracking-wider text-gray-700 dark:text-primary-200 font-semibold mb-1.5">
            Spannungsband (L-N) · {resultVariant === 'baseline' ? 'ohne' : 'mit'} Änderungen
          </h4>
          <div className="rounded-lg p-2.5 bg-gray-50 dark:bg-primary-700 border border-gray-200 dark:border-primary-600">
            <div className="relative h-3.5 rounded-full bg-gray-200 dark:bg-primary-600 mb-1">
              <div className="absolute h-full bg-green-100 dark:bg-green-900/20 rounded-full"
                style={{ left: `${toPercentV(VOLTAGE_WARNING_LOW_V)}%`, width: `${toPercentV(VOLTAGE_WARNING_HIGH_V) - toPercentV(VOLTAGE_WARNING_LOW_V)}%` }} />
              <div className="absolute h-full w-px bg-gray-400 dark:bg-primary-400/60" style={{ left: `${centerPos}%` }} />
              <div className={`absolute h-full rounded-full ${rangeBg} opacity-80`}
                style={{ left: `${minPos}%`, width: `${rangeWidth}%` }} />
            </div>
            <div className="flex justify-between text-[10px] text-gray-500 dark:text-primary-500 mb-1.5 px-0.5">
              <span>{fmt(VOLTAGE_OPERATIONAL_MIN_V, 0)}</span><span>{fmt(VOLTAGE_OPERATIONAL_MIN_V + tickStep, 0)}</span><span>{fmt(NOMINAL_VOLTAGE_LN_V, 0)}</span><span>{fmt(VOLTAGE_OPERATIONAL_MAX_V - tickStep, 0)}</span><span>{fmt(VOLTAGE_OPERATIONAL_MAX_V, 0)}</span>
            </div>
            <div className="grid grid-cols-2 gap-1 text-xs">
              <div>
                <span className="text-gray-500 dark:text-primary-400">Min </span>
                <span className={`font-bold ${getColor(minV)}`}>{fmt(puToVolts(minV), 0)} V</span>
                <span className="text-gray-400 dark:text-primary-500 text-[10px] ml-0.5">{fmt(puDeviationPct(minV), 1)}%</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-primary-400">Max </span>
                <span className={`font-bold ${getColor(maxV)}`}>{fmt(puToVolts(maxV), 0)} V</span>
                <span className="text-gray-400 dark:text-primary-500 text-[10px] ml-0.5">{fmt(puDeviationPct(maxV), 1)}%</span>
              </div>
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-xs">
              {criticalCount > 0 ? (
                <><span className="w-2 h-2 rounded-full bg-red-500" /><span className="text-red-700 dark:text-red-400 font-medium">{criticalCount} außerhalb {VOLTAGE_BAND_LABEL}</span></>
              ) : warningCount > 0 ? (
                <><span className="w-2 h-2 rounded-full bg-orange-500" /><span className="text-orange-700 dark:text-orange-400 font-medium">{warningCount} nahe Bandgrenze</span></>
              ) : (
                <><span className="w-2 h-2 rounded-full bg-green-500" /><span className="text-green-700 dark:text-green-400 font-medium">Normbereich</span></>
              )}
            </div>
          </div>
        </div>

        {/* Confidence */}
        {warnings.length > 0 && (
          <ul className="mb-2 space-y-1">
            {warnings.map(c => (
              <li key={c.factor} className="text-[11px] leading-snug text-amber-700 dark:text-amber-300 flex gap-1.5">
                <span className="shrink-0">▲</span><span>{c.reason}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex gap-2">
          <button
            onClick={() => setShowDetailsModal(true)}
            className="flex-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-300 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700 rounded-lg text-xs font-medium transition-all duration-200 focus:outline-hidden focus:ring-2 focus:ring-blue-500"
          >
            Details
          </button>
          <button
            onClick={clearResults}
            className="flex-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300 dark:bg-primary-700 dark:hover:bg-primary-600 dark:text-primary-100 dark:border-primary-500 rounded-lg text-xs font-medium transition-all duration-200 focus:outline-hidden focus:ring-2 focus:ring-accent-primary"
          >
            Zurücksetzen
          </button>
        </div>
      </div>

      {showDetailsModal && (
        <StressTestDetailsModal
          results={stressTestResults}
          onClose={() => setShowDetailsModal(false)}
          isControlActive={isControlActive}
        />
      )}
    </div>
  );
}
